import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { BellIcon } from "@/components/ui/icons";
import {
  desktopAlertPermission,
  requestDesktopAlerts,
  type DesktopAlertPermission,
} from "@/lib/desktop-alerts";

/**
 * "Not now" is kept in `localStorage` for a week, so closing the browser does
 * not bring the dialog back. It is the only place in the panel to turn alerts
 * on, so it does come back eventually.
 */
const DISMISSED_KEY = "aa.desktop-alerts-dismissed";
const DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000;

/** Long enough for the page to land first, so the dialog is not the first thing painted. */
const OPEN_DELAY_MS = 1200;

function wasDismissed(): boolean {
  try {
    const until = Number(localStorage.getItem(DISMISSED_KEY));
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now() + DISMISS_FOR_MS));
  } catch {
    // Asks again on the next page load; harmless.
  }
}

/**
 * Asks for desktop alerts on arrival, while they are not allowed.
 *
 * An in-app dialog in front of the browser's own prompt, for two reasons. The
 * browser only shows its prompt in response to a click, so something has to be
 * clicked; and a bare browser prompt on load is the one people deny on reflex,
 * after which the site can never ask again. This one says what the alerts are
 * for before the browser asks.
 *
 * Once denied, the browser will not prompt again, so the dialog says where to
 * change it instead of offering a button that would do nothing.
 */
export function DesktopAlertsDialog({
  enabled,
  onGranted,
}: {
  enabled: boolean;
  /** Called once the browser grants permission — registers it for push. */
  onGranted?: () => void;
}) {
  const [permission, setPermission] = useState<DesktopAlertPermission>(desktopAlertPermission);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || wasDismissed()) return;
    if (permission === "granted" || permission === "unsupported") return;
    const timer = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
    // Only on arrival — a permission change made from this dialog closes it
    // below rather than re-arming the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const close = () => {
    rememberDismissed();
    setOpen(false);
  };

  const allow = async () => {
    const next = await requestDesktopAlerts();
    setPermission(next);
    if (next === "granted") onGranted?.();
    // Denied from the browser prompt: stay open and show how to undo it.
    if (next !== "denied") close();
  };

  const blocked = permission === "denied";

  return (
    <Modal
      open={open}
      onClose={close}
      size="sm"
      title={blocked ? "Desktop alerts are blocked" : "Turn on desktop alerts?"}
      footer={
        blocked ? (
          <Button onClick={close}>Got it</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Not now
            </Button>
            <Button onClick={() => void allow()}>
              <BellIcon className="size-4" />
              Allow
            </Button>
          </>
        )
      }
    >
      <div className="flex items-start gap-3">
        <span className="bg-brand-soft text-brand flex size-10 shrink-0 items-center justify-center rounded-full">
          <BellIcon className="size-5" />
        </span>
        {blocked ? (
          <div className="text-content-muted space-y-2 text-sm leading-relaxed">
            <p>This browser is blocking notifications from the admin panel.</p>
            <p>
              Click the icon at the left of the address bar, set{" "}
              <span className="text-content font-medium">Notifications</span> to{" "}
              <span className="text-content font-medium">Allow</span>, then reload.
            </p>
          </div>
        ) : (
          <p className="text-content-muted text-sm leading-relaxed">
            Get a notification on your computer when another admin sends you a notice — even while
            you are in another window.
          </p>
        )}
      </div>
    </Modal>
  );
}
